import { supabase } from '../lib/supabase';

const notificationPreferencesService = {
  /**
   * Get notification preferences for current user
   * @param {string} userId - User ID
   */
  async getNotificationPreferences(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('notification_preferences')?.eq('id', userId)?.single();

      if (error) throw error;
      return { data: data?.notification_preferences || {}, error: null };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Update notification preferences
   * @param {object} preferences - Notification preferences object
   */
  async updateNotificationPreferences(preferences) {
    try {
      const { data, error } = await supabase?.functions?.invoke('update-notification-preferences', {
        body: { notificationPreferences: preferences },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return { data: null, error };
    }
  },

  /**
   * Test SMS notification
   * @param {string} phoneNumber - Phone number to send test SMS
   */
  async testSMSNotification(phoneNumber) {
    try {
      const message = 'Test notification from DraftIQ. Your SMS notifications are configured correctly!';
      const { data, error } = await supabase?.functions?.invoke('send-sms', {
        body: { to: phoneNumber, message },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error sending test SMS:', error);
      return { data: null, error };
    }
  },

  /**
   * Validate phone number format
   * @param {string} phoneNumber - Phone number to validate
   */
  validatePhoneNumber(phoneNumber) {
    const cleaned = phoneNumber?.replace(/\D/g, '');
    if (cleaned?.length === 10) {
      return { valid: true, formatted: `+1${cleaned}` };
    } else if (cleaned?.length === 11 && cleaned?.startsWith('1')) {
      return { valid: true, formatted: `+${cleaned}` };
    }
    return { valid: false, formatted: phoneNumber };
  },

  /**
   * Get default notification preferences
   */
  getDefaultPreferences() {
    return {
      sms: {
        frequency: 'off',
        phoneNumber: '',
        carrierInfo: '',
      },
      email: {
        morningDigest: false,
        eveningDigest: false,
        frequency: 'instant',
        previewFormat: 'summary',
      },
      criticalAlerts: {
        taskDeadlines: true,
        subscriptionChanges: true,
        agentErrors: true,
        severityLevel: 'high',
      },
      doNotDisturb: {
        enabled: false,
        startTime: '22:00',
        endTime: '08:00',
        timezone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/New_York',
        weekendOverride: false,
        holidayOverride: false,
      },
      budgetAlerts: {
        enabled: false,
        spendingLimit: 1000,
        emailEnabled: true,
        smsEnabled: false,
        thresholds: [80, 90, 100],
      },
    };
  },
};

export default notificationPreferencesService;